import {
  addToast,
  DateInput,
  Select,
  SelectOptionType,
  TextAreaInput,
} from '@octano/global-ui';
import { Col, Container, Row } from 'reactstrap';
import { TranslationsKeys } from '../../../../../locales/translations';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { Button } from '@octano/global-ui';
import { useActionKitImp } from '../../../../../components/ozmoit/common';
import { updateStudentSessionEnrollmentAction } from '../actions';
import dayjs from 'dayjs';
import { useValidations } from '../../../../../hooks/useValidations';
import { StudentRiskEvaluationSessionEnrollment } from '../../../types';

interface UpdateSessionFormProps {
  onClose: () => void;
  riskLevelOptions: SelectOptionType[];
  keepForNextSessionOptions: SelectOptionType[];
  conditionalityOptions: SelectOptionType[];
  situationOptions: SelectOptionType[];
  onSuccess?: () => void;
  sessionEnrollment?: Pick<
    StudentRiskEvaluationSessionEnrollment,
    'id' | 'createdAt'
  >;
  isReadOnly?: boolean;
}

export function UpdateSessionForm({
  onClose,
  onSuccess,
  riskLevelOptions,
  keepForNextSessionOptions,
  conditionalityOptions,
  situationOptions,
  sessionEnrollment,
  isReadOnly = false,
}: UpdateSessionFormProps) {
  const { t } = useTranslation(
    TranslationsKeys.STUDENT_WARNINGS_STUDENT_HISTORY,
  );

  const { msgValidations } = useValidations();

  const texts = useMemo(() => {
    return {
      date: t('studentHistory.updateSessionModal.form.date'),
      observation: t('studentHistory.updateSessionModal.form.observation'),
      situation: t('studentHistory.updateSessionModal.form.situation'),
      continuesInCEIA: t(
        'studentHistory.updateSessionModal.form.continuesInCEIA',
      ),
      riskLevel: t('studentHistory.updateSessionModal.form.riskLevel'),
      conditionality: t(
        'studentHistory.updateSessionModal.form.conditionality',
      ),
      save: t('studentHistory.updateSessionModal.save'),
      cancel: t('studentHistory.updateSessionModal.cancel'),
      success: t('studentHistory.updateSessionModal.success'),
      error: t('studentHistory.updateSessionModal.error'),
    };
  }, [t]);

  const methods = useFormContext();

  const { action: handleOnSubmit, loading } = useActionKitImp(
    updateStudentSessionEnrollmentAction,
    {
      onSuccess: () => {
        addToast({
          icon: 'success',
          color: 'success',
          text: texts.success,
        });
        onClose();
        !!onSuccess && onSuccess();
      },
      onError: {
        unknown: () => {
          addToast({
            icon: 'error',
            color: 'danger',
            text: texts.error,
          });
        },
      },
    },
  );

  return (
    <Container>
      <Row xs={12}>
        <Col xs={12} lg={4}>
          <DateInput
            name="date"
            label={texts.date}
            control={methods.control}
            minDate={
              sessionEnrollment?.createdAt
                ? dayjs(sessionEnrollment.createdAt).toDate()
                : undefined
            }
            disabled={loading || isReadOnly}
            rules={{ required: msgValidations.required }}
          />
        </Col>
        <Col xs={12} lg={4}>
          <Select
            name="continuesInCEIA"
            label={texts.continuesInCEIA}
            control={methods.control}
            options={keepForNextSessionOptions}
            disabled={loading || isReadOnly}
            rules={{ required: msgValidations.required }}
          />
        </Col>
        <Col xs={12} lg={4}>
          <Select
            name="situation"
            label={texts.situation}
            control={methods.control}
            options={situationOptions}
            disabled={loading || isReadOnly}
            rules={{ required: msgValidations.required }}
          />
        </Col>
        <Col xs={12} lg={4}>
          <Select
            name="conditionality"
            label={texts.conditionality}
            control={methods.control}
            options={conditionalityOptions}
            disabled={loading || isReadOnly}
            rules={{ required: msgValidations.required }}
          />
        </Col>
        <Col xs={12} lg={4}>
          <Select
            name="riskLevel"
            label={texts.riskLevel}
            control={methods.control}
            options={riskLevelOptions}
            disabled={loading || isReadOnly}
            rules={{ required: msgValidations.required }}
          />
        </Col>
        <Col xs={12}>
          <TextAreaInput
            name="observation"
            label={texts.observation}
            control={methods.control}
            height={100}
            disabled={loading || isReadOnly}
          />
        </Col>
      </Row>
      <Row xs={12} className="mt-4">
        <Col xs={12} lg={6}>
          <Button text={texts.cancel} onClick={onClose} fullwidth outlined />
        </Col>
        <Col xs={12} lg={6}>
          <Button
            loading={loading}
            disabled={isReadOnly}
            text={texts.save}
            onClick={methods.handleSubmit(
              (data) =>
                !!sessionEnrollment &&
                handleOnSubmit(sessionEnrollment?.id, {
                  date: data.date,
                  observation: data.observation,
                  situation: data.situation.value,
                  continuesInCEIA: data.continuesInCEIA.value,
                  conditionality: data.conditionality.value,
                  riskLevel: data.riskLevel.value,
                }),
            )}
            fullwidth
            type="submit"
          />
        </Col>
      </Row>
    </Container>
  );
}
